import { z } from "zod";
import { LocalDateSchema, PlanningOpaqueIdSchema, PlanningRequestIdSchema } from "./planning";

export const FamilyCycleStateSchema = z.enum(["draft", "planned", "active", "reviewing", "closed"]);

export const CycleCommitmentKindSchema = z.enum(["task", "routine", "event", "custom"]);
export const CycleCommitmentStatusSchema = z.enum(["proposed", "agreed", "changed"]);
export const ReviewNoteCategorySchema = z.enum(["good", "helped", "difficult", "next_change"]);
export const ReviewActionKindSchema = z.enum([
  "create_task",
  "reschedule_task",
  "update_routine",
  "other",
]);

export const CycleParticipantSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    participant_type: z.enum(["adult", "child"]),
    participant_id: PlanningOpaqueIdSchema,
    created_at: z.number().int(),
  })
  .strict();

export const CycleCommitmentSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    kind: CycleCommitmentKindSchema,
    child_id: PlanningOpaqueIdSchema.nullable(),
    task_id: PlanningOpaqueIdSchema.nullable(),
    routine_id: PlanningOpaqueIdSchema.nullable(),
    title: z.string().min(1).max(240),
    child_copy: z.string().min(1).max(160),
    status: CycleCommitmentStatusSchema,
    position: z.number().int().min(0).max(500),
    created_at: z.number().int(),
    updated_at: z.number().int(),
  })
  .strict();

export const ReviewNoteSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    category: ReviewNoteCategorySchema,
    child_id: PlanningOpaqueIdSchema.nullable(),
    note: z.string().min(1).max(2000),
    created_by_actor_id: PlanningOpaqueIdSchema,
    created_at: z.number().int(),
    updated_at: z.number().int(),
  })
  .strict();

export const ReviewActionSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    kind: ReviewActionKindSchema,
    child_id: PlanningOpaqueIdSchema.nullable(),
    summary: z.string().min(1).max(2000),
    target_type: z.enum(["task", "routine"]).nullable(),
    target_id: PlanningOpaqueIdSchema.nullable(),
    status: z.enum(["proposed", "applied", "dismissed"]),
    command_id: PlanningRequestIdSchema.nullable(),
    created_at: z.number().int(),
    updated_at: z.number().int(),
  })
  .strict()
  .refine(
    (value) =>
      (value.target_type === null && value.target_id === null) ||
      (value.target_type !== null && value.target_id !== null),
    { message: "target_type and target_id must be set together", path: ["target_id"] },
  )
  .refine((value) => value.status !== "applied" || value.command_id !== null, {
    message: "an applied action requires command_id",
    path: ["command_id"],
  })
  .refine(
    (value) =>
      value.kind === "create_task"
        ? value.target_type === null || value.target_type === "task"
        : value.kind === "reschedule_task"
          ? value.target_type === "task"
          : value.kind === "update_routine"
            ? value.target_type === "routine"
            : value.target_type === null && value.status !== "applied",
    { message: "action kind and target type must match", path: ["target_type"] },
  );

export const FamilyCycleSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    workspace_id: PlanningOpaqueIdSchema,
    start_local_date: LocalDateSchema,
    end_local_date: LocalDateSchema,
    state: FamilyCycleStateSchema,
    plan_summary: z.string().max(8000),
    review_summary: z.string().max(8000),
    participants: z.array(CycleParticipantSchema).max(40),
    commitments: z.array(CycleCommitmentSchema).max(200),
    review_notes: z.array(ReviewNoteSchema).max(200),
    review_actions: z.array(ReviewActionSchema).max(100),
    version: z.number().int().min(1),
    created_at: z.number().int(),
    updated_at: z.number().int(),
    closed_at: z.number().int().nullable(),
  })
  .strict();

const uniqueChildIds = (value: { participant_child_ids: string[] }): boolean =>
  new Set(value.participant_child_ids).size === value.participant_child_ids.length;

export const CycleCommitmentInputSchema = z
  .object({
    kind: CycleCommitmentKindSchema,
    child_id: PlanningOpaqueIdSchema.nullable().default(null),
    task_id: PlanningOpaqueIdSchema.nullable().default(null),
    routine_id: PlanningOpaqueIdSchema.nullable().default(null),
    title: z.string().trim().min(1).max(240),
    child_copy: z.string().trim().min(1).max(160),
    status: CycleCommitmentStatusSchema.default("proposed"),
  })
  .strict()
  .refine(
    (value) => (value.kind === "task" ? value.task_id !== null && value.routine_id === null : true),
    {
      message: "a task commitment requires task_id only",
      path: ["task_id"],
    },
  )
  .refine(
    (value) =>
      value.kind === "routine" ? value.routine_id !== null && value.task_id === null : true,
    { message: "a routine commitment requires routine_id only", path: ["routine_id"] },
  )
  .refine(
    (value) =>
      ["event", "custom"].includes(value.kind)
        ? value.task_id === null && value.routine_id === null
        : true,
    { message: "event and custom commitments cannot reference a Task or Routine", path: ["kind"] },
  );

export const ReviewNoteInputSchema = z
  .object({
    category: ReviewNoteCategorySchema,
    child_id: PlanningOpaqueIdSchema.nullable().default(null),
    note: z.string().trim().min(1).max(2000),
  })
  .strict();

export const ReviewActionInputSchema = z
  .object({
    kind: ReviewActionKindSchema,
    child_id: PlanningOpaqueIdSchema.nullable().default(null),
    summary: z.string().trim().min(1).max(2000),
    target_type: z.enum(["task", "routine"]).nullable().default(null),
    target_id: PlanningOpaqueIdSchema.nullable().default(null),
  })
  .strict()
  .refine(
    (value) =>
      (value.target_type === null && value.target_id === null) ||
      (value.target_type !== null && value.target_id !== null),
    { message: "target_type and target_id must be set together", path: ["target_id"] },
  )
  .refine(
    (value) =>
      value.kind === "create_task" || value.kind === "other"
        ? value.target_type === null
        : value.kind === "reschedule_task"
          ? value.target_type === "task"
          : value.target_type === "routine",
    { message: "action kind and target type must match", path: ["target_type"] },
  );

const cycleDateRange = (value: { start_local_date: string; end_local_date: string }): boolean =>
  value.end_local_date >= value.start_local_date;

export const CreateFamilyCycleRequestSchema = z
  .object({
    start_local_date: LocalDateSchema,
    end_local_date: LocalDateSchema,
    plan_summary: z.string().trim().max(8000).default(""),
    participant_child_ids: z.array(PlanningOpaqueIdSchema).min(1).max(20),
    commitments: z.array(CycleCommitmentInputSchema).max(200).default([]),
  })
  .strict()
  .refine(cycleDateRange, {
    message: "end_local_date must not be earlier than start_local_date",
    path: ["end_local_date"],
  })
  .refine(uniqueChildIds, {
    message: "participant_child_ids must be unique",
    path: ["participant_child_ids"],
  })
  .refine(
    (value) =>
      value.commitments.every(
        (commitment) =>
          commitment.child_id === null || value.participant_child_ids.includes(commitment.child_id),
      ),
    { message: "commitment child must participate in the Cycle", path: ["commitments"] },
  );

export const UpdateFamilyCyclePlanRequestSchema = CreateFamilyCycleRequestSchema.extend({
  expected_version: z.number().int().min(1),
}).strict();

export const UpdateFamilyCycleReviewRequestSchema = z
  .object({
    expected_version: z.number().int().min(1),
    review_summary: z.string().trim().max(8000).default(""),
    notes: z.array(ReviewNoteInputSchema).max(200),
    actions: z.array(ReviewActionInputSchema).max(100),
  })
  .strict();

export const FamilyCycleListResponseSchema = z
  .object({ items: z.array(FamilyCycleSchema).max(100), request_id: PlanningRequestIdSchema })
  .strict();

export const FamilyCycleResponseSchema = z
  .object({ cycle: FamilyCycleSchema, request_id: PlanningRequestIdSchema })
  .strict();

export const ChildCycleCommitmentSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    child_copy: z.string().min(1).max(160),
    status: CycleCommitmentStatusSchema,
  })
  .strict();

export const ChildCurrentFamilyCycleSchema = z
  .object({
    id: PlanningOpaqueIdSchema,
    start_local_date: LocalDateSchema,
    end_local_date: LocalDateSchema,
    state: z.enum(["planned", "active", "reviewing"]),
    commitments: z.array(ChildCycleCommitmentSchema).max(200),
  })
  .strict();

export const ChildCurrentFamilyCycleResponseSchema = z
  .object({
    child_id: PlanningOpaqueIdSchema,
    local_date: LocalDateSchema,
    cycle: ChildCurrentFamilyCycleSchema.nullable(),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const FamilyCycleMutationResponseSchema = z
  .object({
    cycle: FamilyCycleSchema,
    idempotency_replayed: z.boolean(),
    sync_cursor: z.string().min(1),
    request_id: PlanningRequestIdSchema,
  })
  .strict();

export const FamilyCycleCommandSchema = z.enum([
  "plan",
  "return_to_draft",
  "activate",
  "start_review",
  "return_to_active",
  "close",
  "reopen_review",
]);

export const FamilyCycleCommandRequestSchema = z
  .object({ expected_version: z.number().int().min(1) })
  .strict();

export const FamilyCycleCommandResponseSchema = FamilyCycleMutationResponseSchema.extend({
  command: FamilyCycleCommandSchema,
}).strict();

export const ApplyReviewActionRequestSchema = z
  .object({
    expected_version: z.number().int().min(1),
    command_request_id: PlanningRequestIdSchema,
    target_type: z.enum(["task", "routine"]),
    target_id: PlanningOpaqueIdSchema,
  })
  .strict();

export const DismissReviewActionRequestSchema = z
  .object({
    expected_version: z.number().int().min(1),
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export const ReviewActionCommandSchema = z.enum(["apply", "dismiss"]);

export const ReviewActionCommandResponseSchema = FamilyCycleMutationResponseSchema.extend({
  action_id: PlanningOpaqueIdSchema,
  command: ReviewActionCommandSchema,
}).strict();

export type FamilyCycle = z.infer<typeof FamilyCycleSchema>;
export type ChildCurrentFamilyCycle = z.infer<typeof ChildCurrentFamilyCycleSchema>;
export type CreateFamilyCycleRequest = z.infer<typeof CreateFamilyCycleRequestSchema>;
export type UpdateFamilyCyclePlanRequest = z.infer<typeof UpdateFamilyCyclePlanRequestSchema>;
export type UpdateFamilyCycleReviewRequest = z.infer<typeof UpdateFamilyCycleReviewRequestSchema>;
export type ApplyReviewActionRequest = z.infer<typeof ApplyReviewActionRequestSchema>;
export type DismissReviewActionRequest = z.infer<typeof DismissReviewActionRequestSchema>;
